import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NO_ACCESS, PlanAccess, PlanCard, PlanCommand, Tone, planCommands, relativeDay, startOfToday } from '../domain/plan-presentation';

export type { PlanCommand } from '../domain/plan-presentation';

/** Tailwind classes per tone. Kept as whole strings so Tailwind's scanner finds them. */
export const TONE_TAG: Record<Tone, string> = {
  neutral: 'tw-bg-slate-500/20 tw-text-slate-200',
  info: 'tw-bg-sky-500/15 tw-text-sky-300',
  progress: 'tw-bg-blue-500/20 tw-text-blue-300',
  warning: 'tw-bg-amber-500/20 tw-text-amber-300',
  success: 'tw-bg-emerald-500/20 tw-text-emerald-300',
  muted: 'tw-bg-zinc-500/20 tw-text-zinc-400',
  danger: 'tw-bg-red-500/20 tw-text-red-300'
};

const TONE_ACCENT: Record<Tone, string> = {
  neutral: 'tw-border-l-slate-500',
  info: 'tw-border-l-sky-400',
  progress: 'tw-border-l-blue-500',
  warning: 'tw-border-l-amber-400',
  success: 'tw-border-l-emerald-500',
  muted: 'tw-border-l-zinc-600',
  danger: 'tw-border-l-red-500'
};

@Component({
  selector: 'app-plan-card',
  standalone: true,
  imports: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let c = card();
    <article
      class="tw-flex tw-h-full tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-l-4 tw-border-solid tw-border-[color:var(--surface-border)] tw-bg-[var(--surface-card)] tw-transition-colors hover:tw-border-[color:var(--primary-color)] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-[color:var(--primary-color)]"
      [class]="accent()"
      [class.tw-opacity-60]="!c.status.active"
      tabindex="0"
      role="button"
      [attr.aria-label]="'Open ' + c.title"
      (click)="open.emit()"
      (keydown.enter)="open.emit()"
      (keydown.space)="$event.preventDefault(); open.emit()">

      <!-- Title and status -->
      <header class="tw-flex tw-items-start tw-gap-2 tw-px-4 tw-pt-4">
        <h3 class="tw-m-0 tw-line-clamp-2 tw-min-h-[2.6rem] tw-flex-1 tw-text-[15px] tw-font-medium tw-leading-snug"
            [title]="c.title">
          {{ c.title }}
        </h3>
        <span class="tw-shrink-0 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold" [class]="toneTag[c.status.tone]">
          {{ c.status.label }}
        </span>
      </header>

      <!-- What kind of plan, where it came from -->
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-4 tw-pt-2 tw-text-[11px]">
        <span class="tw-rounded tw-bg-[var(--surface-d)] tw-px-1.5 tw-py-0.5 tw-uppercase tw-tracking-wide">{{ c.kind }}</span>
        <span class="tw-rounded tw-px-1.5 tw-py-0.5"
              [class]="c.plan.CreationOrigin === 'Rba' ? 'tw-bg-violet-500/25 tw-text-violet-200' : 'tw-bg-[var(--surface-d)] tw-text-[color:var(--text-color-secondary)]'">
          {{ c.origin }}
        </span>
        @if (c.plan.Kind === 'Periodic' && c.plan.CycleNumber > 0) {
          <span class="tw-text-[color:var(--text-color-secondary)]">Cycle {{ c.plan.CycleNumber }}</span>
        }
        @if (c.recurrence) {
          <span class="tw-text-[color:var(--text-color-secondary)]">· {{ c.recurrence }}</span>
        }
      </div>

      <dl class="tw-m-0 tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-px-4 tw-pb-4 tw-pt-3">
        <!-- When -->
        <div class="tw-flex tw-items-baseline tw-gap-3">
          <dt class="tw-w-16 tw-shrink-0 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-[color:var(--text-color-secondary)]">Target</dt>
          <dd class="tw-m-0 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2">
            <span class="tw-text-sm tw-font-semibold tw-tabular-nums">{{ c.target | date: 'dd MMM yyyy' }}</span>
            @if (c.overdue) {
              <span class="tw-text-[11px] tw-font-medium tw-text-red-300">
                <i class="pi pi-exclamation-circle tw-text-[10px]"></i> Window closed {{ relative(c.windowEnd) }}
              </span>
            } @else if (c.status.active) {
              <span class="tw-text-[11px] tw-text-[color:var(--text-color-secondary)]">{{ relative(c.target) }}</span>
            }
          </dd>
        </div>
        <div class="tw-flex tw-items-baseline tw-gap-3">
          <dt class="tw-w-16 tw-shrink-0 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-[color:var(--text-color-secondary)]">Window</dt>
          <dd class="tw-m-0 tw-text-xs tw-tabular-nums tw-text-[color:var(--text-color-secondary)]">
            {{ c.windowStart | date: 'dd MMM yyyy' }} – {{ c.windowEnd | date: 'dd MMM yyyy' }}
          </dd>
        </div>

        <!-- Progress, from the server's counts -->
        <div class="tw-flex tw-items-start tw-gap-3">
          <dt class="tw-w-16 tw-shrink-0 tw-pt-0.5 tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-[color:var(--text-color-secondary)]">{{ c.noun.title }}</dt>
          <dd class="tw-m-0 tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5">
            @if (c.progress.planned > 0) {
              <span class="tw-text-sm tw-tabular-nums">
                {{ c.progress.executed }} of {{ c.progress.planned }} executed
                @if (c.progress.skipped > 0) {
                  <span class="tw-text-xs tw-text-amber-300"> · {{ c.progress.skipped }} skipped</span>
                }
              </span>
              <div class="tw-flex tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-white/10"
                   role="progressbar" aria-valuemin="0" [attr.aria-valuemax]="c.progress.planned"
                   [attr.aria-valuenow]="c.progress.executed"
                   [attr.aria-label]="c.noun.title + ' executed'">
                <div class="tw-h-full"
                     [class]="c.progress.executedPct === 100 ? 'tw-bg-emerald-500' : 'tw-bg-sky-500'"
                     [style.width.%]="c.progress.executedPct"></div>
                <div class="tw-h-full tw-bg-amber-400/70" [style.width.%]="c.progress.skippedPct"></div>
              </div>
            } @else {
              <span class="tw-text-sm tw-text-[color:var(--text-color-secondary)]">No {{ c.noun.many }} outlined</span>
            }
            @if (c.progress.cancelled > 0) {
              <span class="tw-text-[11px] tw-text-[color:var(--text-color-secondary)]">{{ c.progress.cancelled }} cancelled</span>
            }
          </dd>
        </div>
      </dl>

      @if (commands().length) {
        <!-- Icons only; each one slides its name out on hover or keyboard focus. -->
        <footer class="tw-flex tw-items-center tw-gap-0.5 tw-border-0 tw-border-t tw-border-solid tw-border-[color:var(--surface-border)] tw-px-2 tw-py-1.5"
                (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
          @for (cmd of commands(); track cmd.id) {
            <button type="button"
                    class="tw-group tw-flex tw-h-7 tw-items-center tw-rounded-full tw-border-0 tw-bg-transparent tw-px-2 tw-text-xs tw-transition-colors hover:tw-bg-white/5 focus-visible:tw-bg-white/5 focus-visible:tw-outline focus-visible:tw-outline-1 focus-visible:tw-outline-[color:var(--primary-color)]"
                    [class]="cmd.primary ? 'tw-font-semibold tw-text-[color:var(--primary-color)]'
                      : cmd.danger ? 'tw-text-[color:var(--text-color-secondary)] hover:tw-text-red-300 focus-visible:tw-text-red-300'
                      : 'tw-text-[color:var(--text-color-secondary)] hover:tw-text-[color:var(--text-color)] focus-visible:tw-text-[color:var(--text-color)]'"
                    [attr.aria-label]="cmd.label"
                    (click)="command.emit(cmd.id)">
              <i class="pi tw-text-[13px]" [class]="cmd.icon" aria-hidden="true"></i>
              <span class="tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap tw-opacity-0 tw-transition-all tw-duration-200 tw-ease-out group-hover:tw-ml-1.5 group-hover:tw-max-w-[9rem] group-hover:tw-opacity-100 group-focus-visible:tw-ml-1.5 group-focus-visible:tw-max-w-[9rem] group-focus-visible:tw-opacity-100"
                    aria-hidden="true">{{ cmd.label }}</span>
            </button>
          }
        </footer>
      }
    </article>
  `
})
export class PlanCardComponent {
  readonly card = input.required<PlanCard>();
  /** What the user's permissions allow on this asset's plans. */
  readonly access = input<PlanAccess>(NO_ACCESS);

  readonly open = output<void>();
  readonly command = output<PlanCommand>();

  protected readonly toneTag = TONE_TAG;
  private readonly today = startOfToday();

  protected readonly accent = computed(() => {
    const c = this.card();
    return TONE_ACCENT[c.overdue ? 'danger' : c.status.tone];
  });

  /** Only what the server says this plan allows right now. */
  protected readonly commands = computed(() => planCommands(this.card().plan, this.access()));

  protected relative(date: Date | null): string {
    return relativeDay(date, this.today);
  }
}
